import { plural, t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import { type Point } from 'highcharts';
import { useMemo } from 'react';

import {
  type OpportunityInfluencedDataType,
  type OpportunityStatusOldType,
} from '@/api/opportunity';
import Flare, { Axis, Column, Legend, Tooltip } from '@/components/Flare';
import { type DateRangeType } from '@/components/page';
import { CLOSED_WON_PRIMARY, PIPELINE_PRIMARY } from '@/constants/colors';
import {
  DateISOFormat,
  MonthDateFormat,
  MonthDateShortFormat,
  MonthDateYearFormat,
} from '@/constants/formats';
import type SolAPIError from '@/error/SolAPIError';
import { formatDate } from '@/helper/dateHelper';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  opportunityType: OpportunityStatusOldType;
  data?: OpportunityInfluencedDataType;
  isLoading?: boolean;
  error?: SolAPIError | null;
  onSelectedDateRangeChange?: (dateRange?: DateRangeType) => void;
};

const OpportunityInfluenceChart = ({
  opportunityType,
  data,
  isLoading = false,
  error,
  onSelectedDateRangeChange,
}: Props) => {
  const seriesName = opportunityType === 'closedWon' ? t`Closed Won` : t`Pipeline`;

  const seriesData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return [
      {
        id: 'opportunity',
        name: seriesName,
        zIndex: 1,
        data: data[opportunityType].data.map((d) => ({
          amount: d.revenue,
          custom: { date: d.sundayOfWeek, count: d.opportunities },
        })),
      },
    ];
  }, [data, seriesName, opportunityType]);

  const handleSelectionChange = (point: Point | null) => {
    const startDate = point?.options.custom?.date;
    if (startDate) {
      const endDate = dayjs(startDate).add(6, 'd');
      onSelectedDateRangeChange?.({
        startDate: dayjs(startDate).format(DateISOFormat),
        endDate: endDate.format(DateISOFormat),
      });
    } else {
      onSelectedDateRangeChange?.();
    }
  };

  return (
    <div style={{ height: 400 }}>
      <Flare
        data={seriesData}
        description={t`A chart showing number of opportunities each week over the select time period`}
        colors={[opportunityType === 'closedWon' ? CLOSED_WON_PRIMARY : PIPELINE_PRIMARY]}
        isLoading={isLoading}
        error={error}
      >
        <Legend noToggleVisibility />
        <Column
          y="amount"
          position="stack"
          skeletonLoaderOptions={{ numCategories: 12, numSeries: 1 }}
          onPointSelectionChange={onSelectedDateRangeChange ? handleSelectionChange : undefined}
        />
        <Axis
          position="left"
          labelFormat={(item) =>
            numberFormat(item.value, { isCurrency: true, compact: true, precision: 0 })
          }
          title={t`${seriesName} Amount`}
        />
        <Axis
          position="bottom"
          categories={data?.[opportunityType].data.map((d) => d.sundayOfWeek)}
          labelFormat={(item) => formatDate(item.value, MonthDateShortFormat, { isUTC: true })}
          crosshair="rect"
        />
        <Tooltip
          titleFormat={(item) => {
            const startDate = dayjs(item?.custom?.date).utc();
            const endDate = startDate.add(6, 'd');
            return `${startDate.format(MonthDateFormat)} - ${endDate.format(MonthDateYearFormat)}`;
          }}
          rowSecondaryValueFormat={(point) =>
            `(${plural(point?.custom?.count, {
              one: '# Opportunity',
              other: '# Opportunities',
            })})`
          }
          rowValueFormat={(point) => numberFormat(point?.y, { isCurrency: true, precision: 0 })}
          stickyOnClick={!onSelectedDateRangeChange}
          shared
        />
      </Flare>
    </div>
  );
};

export default OpportunityInfluenceChart;
